/**
 * CashPanel — weekly payroll + cash on hand + monthly overhead (company_settings).
 * Non-Soren path to the same inputs used by get_cash_forecast / A6.
 */
import { useEffect, useState } from 'react'
import { saveSettings, type CompanySettings } from '../../../api/settings'
import MoneyInput from '../../../components/MoneyInput'
import { formatMoneyInputBlur } from '../../../lib/formatters'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

interface Props {
  settings: CompanySettings | null
  onSaved: (s: CompanySettings) => void
}

function centsToDollarsInput(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(Number(cents))) return ''
  return formatMoneyInputBlur(String(Number(cents) / 100))
}

function dollarsToCents(raw: string): number | null {
  const n = parseFloat(String(raw).replace(/[$,\s]/g, ''))
  if (!Number.isFinite(n)) return null
  return Math.round(n * 100)
}

export default function CashPanel({ settings, onSaved }: Props) {
  const [payroll, setPayroll] = useState('')
  const [bank, setBank] = useState('')
  const [overhead, setOverhead] = useState('')
  const [asOf, setAsOf] = useState<string | null>(null)
  const [status, setStatus] = useState<SaveStatus>('idle')
  const [errMsg, setErrMsg] = useState('')

  useEffect(() => {
    if (!settings) return
    setPayroll(centsToDollarsInput(settings.weekly_payroll_cents))
    setBank(centsToDollarsInput(settings.cash_on_hand_cents))
    setOverhead(centsToDollarsInput(settings.monthly_overhead_cents))
    setAsOf(settings.cash_on_hand_as_of || null)
  }, [settings])

  async function handleSave() {
    const payrollCents = dollarsToCents(payroll)
    const bankCents = dollarsToCents(bank)
    const overheadCents = dollarsToCents(overhead)
    if (payroll.trim() && payrollCents == null) {
      setErrMsg('Weekly payroll must be a dollar amount')
      setStatus('error')
      setTimeout(() => setStatus('idle'), 4000)
      return
    }
    if (bank.trim() && bankCents == null) {
      setErrMsg('Cash on hand must be a dollar amount')
      setStatus('error')
      setTimeout(() => setStatus('idle'), 4000)
      return
    }
    if (overhead.trim() && overheadCents == null) {
      setErrMsg('Monthly overhead must be a dollar amount')
      setStatus('error')
      setTimeout(() => setStatus('idle'), 4000)
      return
    }
    setStatus('saving')
    try {
      const updated = await saveSettings({
        weekly_payroll_cents: payroll.trim() ? payrollCents : null,
        cash_on_hand_cents: bank.trim() ? bankCents : null,
        monthly_overhead_cents: overhead.trim() ? overheadCents : null,
      })
      onSaved(updated)
      setAsOf(updated.cash_on_hand_as_of || null)
      setStatus('saved')
      setTimeout(() => setStatus('idle'), 2500)
    } catch (e) {
      setErrMsg((e as Error).message || 'Save failed')
      setStatus('error')
      setTimeout(() => setStatus('idle'), 4000)
    }
  }

  const saveLabel = status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved ✓' : 'Save cash'

  return (
    <div id="cash" className="panel" data-testid="panel-cash" style={{ marginTop: 14 }}>
      <div className="panel-h">
        <div className="panel-tt">Cash</div>
        <span className="panel-st">Bank, payroll, and monthly overhead for Soren cash forecast</span>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Cash on hand</div>
          <div className="set-row-st">
            Bank / available cash
            {asOf ? ` · as of ${asOf}` : ' · as-of date set on save'}
          </div>
        </div>
        <div className="set-row-r">
          <span style={{ fontSize: 13, color: 'var(--ink3)', marginRight: 4 }}>$</span>
          <MoneyInput
            className="fld-i mono"
            value={bank}
            onChange={setBank}
            placeholder="62000.00"
            style={{ width: 140, padding: '6px 10px', textAlign: 'right' }}
            data-testid="cash-on-hand-input"
          />
        </div>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Weekly payroll</div>
          <div className="set-row-st">Typical weekly burn used in the forecast</div>
        </div>
        <div className="set-row-r">
          <span style={{ fontSize: 13, color: 'var(--ink3)', marginRight: 4 }}>$</span>
          <MoneyInput
            className="fld-i mono"
            value={payroll}
            onChange={setPayroll}
            placeholder="50000.00"
            style={{ width: 140, padding: '6px 10px', textAlign: 'right' }}
            data-testid="weekly-payroll-input"
          />
        </div>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Monthly overhead</div>
          <div className="set-row-st">
            Rent, credit cards, fixed monthly burn (converted to weekly in the forecast)
          </div>
        </div>
        <div className="set-row-r">
          <span style={{ fontSize: 13, color: 'var(--ink3)', marginRight: 4 }}>$</span>
          <MoneyInput
            className="fld-i mono"
            value={overhead}
            onChange={setOverhead}
            placeholder="5000.00"
            style={{ width: 140, padding: '6px 10px', textAlign: 'right' }}
            data-testid="monthly-overhead-input"
          />
        </div>
      </div>

      {asOf ? (
        <div className="set-row" data-testid="cash-as-of-row">
          <div className="set-row-l">
            <div className="set-row-tt">As of</div>
            <div className="set-row-st">Read-only — updated when you save cash on hand</div>
          </div>
          <div className="set-row-r">
            <span className="mono" style={{ fontSize: 13 }}>{asOf}</span>
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          className="btn b-dk btn-sm"
          data-testid="cash-save"
          disabled={status === 'saving'}
          onClick={() => void handleSave()}
        >
          {saveLabel}
        </button>
        {status === 'error' ? (
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{errMsg}</span>
        ) : null}
      </div>
    </div>
  )
}
